// Corpus-free category definitions — safe for client bundles.
export type NotaryFaqCategory =
  | "apostille"
  | "embassy"
  | "cost"
  | "documents"
  | "mobile"
  | "urgent"
  | "process";

export interface CategoryDef {
  slug: NotaryFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const NOTARY_FAQ_CATEGORIES: CategoryDef[] = [
  {
    slug: "apostille",
    titleTh: "Apostille & กงสุลรับรอง",
    titleEn: "Apostille & MFA Legalization",
    descTh: "คำถามเกี่ยวกับ Apostille Hague Convention 2026 และการรับรองที่กระทรวงการต่างประเทศ",
    descEn: "Questions on Apostille (Hague 2026) and Thai MFA / consular legalization.",
    matchTh: /(apostille|hague|กระทรวงการต่างประเทศ|กงสุล|mfa|legaliz)/i,
    matchEn: /(apostille|hague|mfa|legaliz|ministry of foreign|consular)/i,
  },
  {
    slug: "embassy",
    titleTh: "รับรองผ่านสถานทูต",
    titleEn: "Embassy Legalization",
    descTh: "ขั้นตอนการรับรองเอกสารที่สถานทูต / สถานกงสุลของประเทศปลายทาง",
    descEn: "How to legalize documents at foreign embassies in Thailand.",
    matchTh: /(สถานทูต|สถานกงสุล|embassy|consulate)/i,
    matchEn: /(embassy|consulate|legalization at)/i,
  },
  {
    slug: "cost",
    titleTh: "ราคาและค่าบริการ",
    titleEn: "Pricing & Fees",
    descTh: "อัตราค่าบริการ Notary Public ปี 2026 — ต่อเอกสาร, ต่อลายเซ็น, และแพ็กเกจนอกสถานที่",
    descEn: "2026 notary public fees in Thailand — per document, per signature, mobile packages.",
    matchTh: /(ราคา|ค่าบริการ|ค่าธรรมเนียม|กี่บาท|ค่าใช้จ่าย|บาท)/,
    matchEn: /(price|cost|fee|charge|how much|thb|baht)/i,
  },
  {
    slug: "documents",
    titleTh: "ประเภทเอกสารที่รับรอง",
    titleEn: "Document Types",
    descTh: "หนังสือมอบอำนาจ, Affidavit, สำเนา Passport, ทะเบียนบ้าน, และเอกสารบริษัท",
    descEn: "POA, Affidavit, Passport copy, corporate resolutions and more.",
    matchTh: /(หนังสือมอบอำนาจ|affidavit|passport|สำเนา|เอกสารบริษัท|ทะเบียนบ้าน|สัญญา|ใบสมรส|ใบเกิด|วุฒิ)/i,
    matchEn: /(power of attorney|poa|affidavit|passport|copy|corporate|certificate|contract|marriage|birth|degree|diploma)/i,
  },
  {
    slug: "mobile",
    titleTh: "บริการนอกสถานที่ / Mobile Notary",
    titleEn: "Mobile Notary On-site",
    descTh: "เรียก Notary ไปที่บ้าน โรงพยาบาล หรือโรงแรม — ครอบคลุม 77 จังหวัด",
    descEn: "Mobile notary visits across Phuket and all 77 provinces: villas, hotels, hospitals, marinas and offices, including after-hours signings, witness arrangements and same-day sealing.",
    matchTh: /(นอกสถานที่|ถึงที่|ไปที่บ้าน|ไปที่โรงแรม|mobile|on.?site)/i,
    matchEn: /(mobile|on.?site|at your|come to|house call|hotel)/i,
  },
  {
    slug: "urgent",
    titleTh: "บริการด่วน / วันเดียวเสร็จ",
    titleEn: "Same-Day / Urgent",
    descTh: "รับรองด่วนภายในวันเดียว, บริการเสาร์-อาทิตย์, และกรณีฉุกเฉิน",
    descEn: "Same-day, weekend, and emergency notary services.",
    matchTh: /(ด่วน|วันเดียว|เร่งด่วน|เสาร์|อาทิตย์|ฉุกเฉิน)/,
    matchEn: /(urgent|same.?day|rush|emergency|weekend|expedite)/i,
  },
  {
    slug: "process",
    titleTh: "ขั้นตอนและระยะเวลา",
    titleEn: "Process & Timeline",
    descTh: "ขั้นตอนงาน Notary Public ตั้งแต่ส่งเอกสารตรวจ นัดเซ็นต่อหน้าทนาย ประทับตรารับรอง ไปจนถึงรับรองกงสุลหรือ Apostille พร้อมระยะเวลาแต่ละขั้นตอนแบบชัดเจน",
    descEn: "The full notary workflow in Phuket: document review, appointment and signing before a licensed notarial services attorney, sealing, then MFA legalization or Apostille — with realistic timing for each stage.",
    matchTh: /(ขั้นตอน|กี่วัน|ระยะเวลา|ใช้เวลา|process|วิธี)/i,
    matchEn: /(process|step|how long|timeline|procedure|how do i|how to)/i,
  },
];

export function getCategoryDef(slug: string): CategoryDef | undefined {
  return NOTARY_FAQ_CATEGORIES.find((c) => c.slug === slug);
}
